import { useEffect, useState } from "react";
import { generatePath, Link, Redirect } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Container,
  createStyles,
  makeStyles,
  Typography,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import { useTranslation } from 'react-i18next';

/** Import components */
import { MyAppBar } from "../appbar/MyAppBar";
import { MyFooter } from "../footer/MyFooter";

/** Import router, app */
import { appPath, paramName } from "../../router/AppRoute";
import { useAppSelector } from "../../app/hooks";

/** Import custom hooks */
import useScrollOverflow from "../../util/UseScrollOverflow";
import { colorUtil } from "../../util/ColorUtil";

/** Import libs */
import { blobToBase64 } from "../../util/Media";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    couponImageDiv: {
      height: "75vw",
      width: "75vw",
      minWidth: "75vw",
      paddingTop: "100%",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
    },
    bottomBox: {
      height: "10vh",
    },
    titleCoupon: {
      fontSize: "32px",
      fontWeight: "bold",
      padding: "26px 30px",
      lineHeight: "24px",
      "@media screen and (max-height: 800px)": {
        fontSize: "28px",
        padding: "22px 35px",
      },
      "@media screen and (max-height: 700px)": {
        fontSize: "24px",
        padding: "20px 30px",
      },
      "@media screen and (max-height: 650px)": {
        fontSize: "20px",
        padding: "16px 25px",
      },
      "@media screen and (max-height: 560px)": {
        fontSize: "18px",
        padding: "14px 25px",
      },
    },
    titleImage: {
      fontSize: "18px",
      fontWeight: "bold",
    },
    subTitleImage: {
      fontSize: "15px",
      fontWeight: "normal",
      overflowWrap: "break-word",
      width: "100%",
    },
  })
);

export const CouponPreview = (): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  const coupon = useAppSelector((state) => state.preview.coupon);
  const { scrollOverflowOn, scrollOverflowOff } = useScrollOverflow();
  const [mediaCoupon, setMediaCoupon] = useState<string>(
    `url(${coupon.imageUrl})`
  );

  useEffect(() => {
    scrollOverflowOn();
    return () => {
      scrollOverflowOff();
    };
  }, [scrollOverflowOn, scrollOverflowOff]);

  useEffect(() => {
    if (coupon.isBase64)
      blobToBase64(coupon.imageUrl as unknown as File, handleSetBase64Blob);
  }, [coupon.isBase64, coupon.imageUrl]);

  const pathInput0 = generatePath(appPath.shopqr.inputAnswerPreview, {
    [paramName.surveyQestionPageIndexStr]: 0,
  });

  if (!coupon.isCoupon) return <Redirect to={pathInput0} />;

  const handleSetBase64Blob = (base64: string) => {
    setMediaCoupon("url(" + base64 + ")");
  };

  return (
    <Container
      maxWidth={false}
      disableGutters={true}
      className={classes.container}
    >
      <MyAppBar />
      <Box position="relative" flexGrow="1">
        {/* 冒頭メッセージ begin */}
        <Box mt={4} display="none">
          <Typography variant="h5" align="center">
            <Box fontWeight="fontWeightBold">{t('preview.here_today_coupon')}</Box>
          </Typography>
        </Box>
        {/* 冒頭メッセージ end */}

        {/* チャレンジメッセージ begin */}
        <Box mt={4} mx={3}>
          <Typography variant="h5" align="center">
            <Box
              fontWeight="fontWeightBold"
              style={{ wordBreak: "break-word" }}
            >
              {coupon.topMessage}
            </Box>
          </Typography>
        </Box>
        {/* チャレンジメッセージ end */}

        {/* クーポン画像エリア begin */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Card>
            <Box m={4} position="relative">
              {/* クーポン画像 begin */}
              <Box
                position="relative"
                className={classes.couponImageDiv}
                style={{
                  backgroundImage: `${mediaCoupon}`,
                }}
              >
                {/* fade container begin */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bgcolor={colorUtil.black4}
                >
                  {/* 「クーポン見本」container begin */}
                  <Box
                    borderRadius="100px"
                    bgcolor={colorUtil.couponSample}
                  >
                    {/* 「クーポン見本」 begin */}
                    <Typography className={classes.titleCoupon}>
                      {t('preview.sample_coupon')}
                    </Typography>
                    {/* 「クーポン見本」 end */}
                  </Box>
                  {/* 「見本 クーポン」container end */}
                </Box>
                {/* fade container end */}

                {/* Copyガード begin */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  &nbsp;
                </Box>
                {/* Copyガード end */}
              </Box>
              {/* クーポン画像 end */}
            </Box>
          </Card>
        </Box>
        {/* クーポン画像エリア end */}

        {/* 注意事項 begin */}
        <Box
          id='coupon-caution'
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            whiteSpace: "pre-line",
            width: "100%",
            px: 3,
            pt: 7,
            mb: 2,
          }}
        >
          <Typography variant='h6' className={classes.titleImage}>
            {t('preview.terms_and_conditions')}
          </Typography>
          <Typography variant='subtitle1' className={classes.subTitleImage}>
            {coupon.caution}
          </Typography>
        </Box>
        {/* 注意事項 end */}

        {/* 回答するButton begin */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="contained"
            color="primary"
            endIcon={<TrendingUpIcon />}
            component={Link}
            to={pathInput0}
          >
            {t('preview.btn.answer')}
          </Button>
        </Box>
        {/* 回答するButton end */}

        {/* 最下部余白 begin */}
        <Box className={classes.bottomBox}>&nbsp;</Box>
        {/* 最下部余白 end */}
      </Box>
      <MyFooter />
    </Container>
  );
};
